import * as React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { getUnprocessedItems } from "../../redux/dynamo_db/actions";
import { downloadCSV, downloadJSON } from "../../utils";

const ENTITY_TYPES = ["serial_number", "license", "order", "system"];

// Admin-only support console over data-pipelines' UnprocessedItems table: rows
// that failed validation in a standard input and were parked with their raw
// body + failure reason instead of being silently dropped. This page only
// reads (search + detail); reprocessing is requested from the detail page,
// which publishes onto data-pipelines' resend queue rather than writing here.
export default function UnprocessedItems() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [entityType, setEntityType] = React.useState("");
  const [errorReason, setErrorReason] = React.useState("");
  const [uniqueId, setUniqueId] = React.useState("");

  const [items, setItems] = React.useState([]);
  const [count, setCount] = React.useState(0);
  const [truncated, setTruncated] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [searched, setSearched] = React.useState(false);

  const runSearch = async () => {
    if (!uniqueId.trim() && !entityType) {
      setError("Provide a Unique ID or select a Type to search.");
      return;
    }
    setRunning(true);
    setError(null);
    const params = {};
    if (uniqueId.trim()) {
      params.unique_id = uniqueId.trim();
    } else {
      params.entity_type = entityType;
      if (errorReason.trim()) {
        params.error_reason = errorReason.trim();
      }
    }
    const acted = await dispatch(getUnprocessedItems(params));
    setRunning(false);
    setSearched(true);
    if (acted?.errors) {
      setError(
        acted.errors.error || `Request failed (${acted.errors.responseCode}).`,
      );
      setItems([]);
      setCount(0);
      setTruncated(false);
      return;
    }
    const res = acted?.unprocessedItems || {};
    setItems(res.items || []);
    setCount(res.count || 0);
    setTruncated(!!res.truncated);
  };

  const handleKeydown = (event) => {
    if (event.key === "Enter") {
      event.stopPropagation();
      event.preventDefault();
      setTimeout(() => {
        runSearch();
      }, 0);
    }
  };

  const handleRowClick = (row) => {
    navigate(
      `/unprocesseditems/details/${row.SK}/${encodeURIComponent(row.PK)}`,
    );
  };

  // (a) Compact summary of every currently-loaded row.
  const handleExportErrors = () => {
    if (!items.length) return;
    const fields = [
      { name: "entity_type", value: "SK" },
      { name: "unique_id", value: "PK" },
      { name: "error_reason" },
      { name: "created_at" },
    ];
    downloadCSV(fields, "unprocessed_items_errors", ",", items);
  };

  // (b) "Ready to reimport": if every loaded row shares the same entity_type,
  // build one CSV whose columns are the union of keys across each row's
  // *parsed* raw_data — droppable straight back into the data-pipelines inputs
  // bucket in the original column format. Otherwise fall back to a combined
  // JSON export (raw_data kept as the original string, not re-parsed).
  const handleExportReimportable = () => {
    if (!items.length) return;
    const entityTypes = new Set(items.map((item) => item.SK));
    if (entityTypes.size === 1) {
      const parsedRows = items.map((item) => {
        try {
          return JSON.parse(item.raw_data);
        } catch {
          return {};
        }
      });
      const seen = new Set();
      const keys = [];
      parsedRows.forEach((row) =>
        Object.keys(row).forEach((key) => {
          if (!seen.has(key)) {
            seen.add(key);
            keys.push(key);
          }
        }),
      );
      const fields = keys.map((key) => ({ name: key }));
      downloadCSV(fields, "unprocessed_items_reimport", ",", parsedRows);
    } else {
      const fields = [
        { name: "entity_type", value: "SK" },
        { name: "unique_id", value: "PK" },
        { name: "raw_data" },
      ];
      downloadJSON(fields, "unprocessed_items_reimport", items);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1, color: "#6b469e" }}>
        Unprocessed Items
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Rows that failed validation in a data-pipelines input and were parked
        for manual review, along with their raw body and failure reason. Search
        by type (with an optional error reason filter), or jump straight to a
        known unique id.
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Type</InputLabel>
          <Select
            label="Type"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
          >
            <MenuItem value="">(any)</MenuItem>
            {ENTITY_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Error reason"
          value={errorReason}
          onChange={(e) => setErrorReason(e.target.value)}
          onKeyDown={handleKeydown}
          disabled={!entityType}
          sx={{ minWidth: 220 }}
        />
        <TextField
          size="small"
          label="Unique ID"
          value={uniqueId}
          onChange={(e) => setUniqueId(e.target.value)}
          onKeyDown={handleKeydown}
          sx={{ minWidth: 260 }}
        />
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          disabled={running}
          onClick={runSearch}
        >
          Search
        </Button>
        <Button
          startIcon={<FileDownloadIcon />}
          disabled={!items.length}
          onClick={handleExportErrors}
        >
          Export errors
        </Button>
        <Button
          startIcon={<FileDownloadIcon />}
          disabled={!items.length}
          onClick={handleExportReimportable}
        >
          Export reimportable
        </Button>
      </Box>

      {running && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {searched && !error && (
        <Chip
          label={`${count} item(s)${truncated ? " — truncated, refine your search" : ""}`}
          sx={{ mb: 1 }}
        />
      )}

      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Unique ID</TableCell>
              <TableCell>Error Reason</TableCell>
              <TableCell>Created At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row, idx) => (
              <TableRow
                key={`${row.PK}#${row.SK}#${idx}`}
                hover
                style={{ cursor: "pointer" }}
                onClick={() => handleRowClick(row)}
              >
                <TableCell>{row.SK}</TableCell>
                <TableCell>{row.PK}</TableCell>
                <TableCell>{row.error_reason}</TableCell>
                <TableCell>{row.created_at}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
